import { useEffect, useMemo, useState } from 'react';
import { getProviderDashboardCriticalAlerts } from '../../api/providerApi';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { KpiCard } from '../../components/ui/KpiCard';
import { IconAlert } from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';

export function ProviderExceptionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getProviderDashboardCriticalAlerts();
        if (!cancelled) setItems(response.items || []);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load exceptions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const columns = useMemo(
    () => [
      {
        key: 'code',
        header: 'Code',
        render: (row) => (
          <span className="font-semibold text-eorig-ink">{row.code || '—'}</span>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        render: (row) => <StatusPill value={row.category} />,
      },
      {
        key: 'severity',
        header: 'Severity',
        render: (row) => <StatusPill value={row.severity} />,
      },
      {
        key: 'message',
        header: 'Message',
        render: (row) => <span className="text-eorig-muted">{row.message || '—'}</span>,
      },
      {
        key: 'created_at',
        header: 'Raised',
        render: (row) => (
          <span className="text-eorig-muted">{formatDateTime(row.created_at)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <KpiCard
        label="Open alerts"
        value={items.length}
        subline="From Provider critical-alerts API"
        icon={IconAlert}
        accent
        loading={loading}
      />
      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} />
      ) : null}
      <DataTable
        columns={columns}
        rows={items}
        rowKey={(row) => `${row.category}-${row.code}-${row.reference_id || row.message || 'alert'}`}
        rowHref={(row) =>
          row.category === 'CONNECTOR'
            ? '/provider/integrations'
            : row.reference_id
              ? `/provider/packages/${row.reference_id}`
              : undefined
        }
        loading={loading}
        emptyMessage="No blocking alerts right now."
        minWidth={640}
      />
    </div>
  );
}
